// src/components/sidebar/Sidebar.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Sidebar as ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconButton,
  useMediaQuery,
  Box,
  Modal,
  Typography,
} from '@mui/material';
import {
  Home,
  Group,
  WorkOutline,
  Event,
  Security,
  Settings,
  Logout,
  Feedback,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import './Sidebar.css';
import { AuthContext } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { useLocation } from 'react-router-dom';

const Sidebar = () => {
  const { collapsed, setCollapsed } = useSidebar();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [visible, setVisible] = useState(!isMobile);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setVisible(!isMobile);
  }, [isMobile]);

  const toggleSidebar = () => setVisible(!visible);
  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', newState);
  };

  const handleRestrictedClick = () => setShowAccessModal(true);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // const renderMenuItem = (label, icon, path) => (
  //   <MenuItem
  //     icon={icon}
  //     onClick={() => {
  //     if (['Profile', 'Home'].includes(label)) {
  //       navigate(path);
  //     } else {
  //       user?.active ? navigate(path) : handleRestrictedClick();
  //     }
  //   }}
  //   >
  //     {label}
  //   </MenuItem>
  // );

  const renderMenuItem = (label, icon, path) => (
    <MenuItem
      icon={icon}
      onClick={() => {
        if (['Profile', 'Home'].includes(label)) {
          navigate(path);
        } else {
          user?.active ? navigate(path) : handleRestrictedClick();
        }
        if (isMobile) setVisible(false);
      }}
      className={location.pathname === path ? 'active-menu-item' : ''}
    >
      {label}
    </MenuItem>
  );

  return (
    <>
      {isMobile && visible && (
        <div className="mobile-sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Hamburger Wrapper - only shown when sidebar is hidden */}
      {isMobile && !visible && (
        <div className="hamburger-wrapper">
          <IconButton
            onClick={toggleSidebar}
            className="hamburger-icon"
            sx={{
              position: 'relative',
              zIndex: 2000,
              background: 'none',
              border: 'none',
            }}
            // aria-label="Open menu"
            aria-label={visible ? 'Close menu' : 'Open menu'}
          >
            <div className="hamburger-lines">
              <span className="line" />
              <span className="line" />
              <span className="line" />
            </div>
          </IconButton>

          <img src="logo.png" alt="Logo" className="hamburger-logo" />
        </div>
      )}

      <div
        className={`sidebar-container ${visible ? 'show' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: isMobile ? '60%' : collapsed ? '80px' : '250px',
          zIndex: 1500,
          backgroundColor: '#f9f9f9',
          transition: 'transform 0.3s ease-in-out, width 0.3s ease-in-out',
          transform: visible ? 'translateX(0)' : 'translateX(-100%)',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '50px',
        }}
      >
        {isMobile && visible && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              position: 'absolute',
              top: 10,
              left: 15,
              zIndex: 2001,
            }}
            aria-label="Close menu"
          >
            <CloseIcon sx={{ color: '#272974', fontSize: 30 }} />
          </IconButton>
        )}

        <ProSidebar collapsed={collapsed} style={{ flexGrow: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: collapsed ? 20 : 24,
              paddingTop: 12,
              paddingBottom: 12,
            }}
          >
            {!isMobile && (
              <IconButton
                onClick={toggleCollapse}
                size="small"
                aria-label="Toggle sidebar"
                sx={{ color: '#272974' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </div>

          <Menu>
            {user?.role === 'admin' || user?.role === 'superadmin' ? (
              <>
                <MenuItem
                  icon={<Group />}
                  onClick={() => {
                    navigate('/AdminDashboard');
                    if (isMobile) setVisible(false);
                  }}
                  className={
                    location.pathname === '/AdminDashboard'
                      ? 'active-menu-item'
                      : ''
                  }
                >
                  Home
                </MenuItem>
                <MenuItem
                  icon={<WorkOutline />}
                  component={<Link to="/AlumniInformation" />}
                >
                  Alumni Info
                </MenuItem>
                <MenuItem icon={<Event />} component={<Link to="/EventPage" />}>
                  Events
                </MenuItem>
                <MenuItem
                  icon={<Security />}
                  component={<Link to="/JobPost" />}
                >
                  JobPost
                </MenuItem>
                <MenuItem
                  icon={<Settings />}
                  component={<Link to="/AccountSetting" />}
                >
                  Settings
                </MenuItem>
                <MenuItem
                  icon={<Feedback />}
                  component={<Link to="/feedBack" />}
                >
                  Feedback
                </MenuItem>
              </>
            ) : (
              <>
                {renderMenuItem('Home', <Home />, '/AlumniDashboard')}
                {renderMenuItem('Profile', <Settings />, '/AlumniProfile')}
                {renderMenuItem('Events', <Event />, '/Events')}
                {renderMenuItem('Jobs', <WorkOutline />, '/Jobs')}
                {renderMenuItem('Feedback', <Feedback />, '/AlumniFeedback')}
              </>
            )}
          </Menu>

          <div style={{ marginTop: 'auto' }}>
            <Menu>
              <MenuItem
                icon={<Logout />}
                style={{ color: 'red' }}
                onClick={handleLogout}
              >
                Log Out
              </MenuItem>
            </Menu>
          </div>
        </ProSidebar>
      </div>

      <Modal open={showAccessModal} onClose={() => setShowAccessModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            // border: '2px solid #272974',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            width: {
              xs: '75%',
              sm: '70%',
              md: '400px', 
            },
          }}
        >
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1">
            Your account has not yet been activated by the administrator.
          </Typography>
          <Box mt={2}>
            <button
              onClick={() => setShowAccessModal(false)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#272974',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default Sidebar;
